/* ------------------------------------------------------------------
 * Jokers Costume Mega Store — cart drawer + /cart page behaviour (Concept v6)
 *
 * 1. Store-pickup availability filter  — hides pickup locations that
 *    cannot fulfil the whole order; shows an explanatory message when
 *    none can.
 * 2. Premium/Express messaging         — swaps the tab-2 copy when the
 *    Rocklea warehouse cannot fulfil the whole order.
 * 3. Rocklea availability flag         — writes the _ml line item property
 *    (ml=no) when Rocklea cannot fulfil the whole cart. ShipZip's Premium
 *    rate is gated on this. _odm is ShipZip's, not ours — see below —
 *    but a line added after the customer chose a tile gets a COPY of a
 *    stamped sibling's ShipZip set, so late adds inherit the choice.
 * 4. Checkout gating                   — the checkout button requires
 *    BOTH a confirmed delivery option and the Conditions of Sale box,
 *    and now also that no routing-flag write is still in flight.
 *    Applied independently to each surface via PAIRS: the drawer and the
 *    /cart page each have their own form, button and checkbox, and a
 *    document-wide selector would cross-wire them.
 * 5. Fast remove                       — removes a drawer line by line
 *    POSITION in a single request.
 *
 * Loaded from layout/theme.liquid. Kept in an asset file (not inline)
 * so a future theme update cannot overwrite it.
 *
 * 2026-07-27 — LAST-ITEM REMOVE FIX. Three defects addressed:
 *   a) applyCartFlags wrote cart_data[0].qty (the FIRST line's quantity)
 *      onto whatever variant it was flagging. Harmless with one line,
 *      corrupting with two or more. Now resolves the variant's own qty
 *      and bails if that variant is no longer in the cart.
 *   b) A flag write could still be in flight when the user removed the
 *      last line. The two /cart/change.js calls collided; the flag write
 *      landed against a variant that no longer existed, and the theme's
 *      error path redirected to /cart. Both blocks now share a lock
 *      (window.JokersCart) — removal waits for pending flag writes, and
 *      flag writes are skipped entirely while a removal is running.
 *   c) The remove handler parsed the response without checking r.ok, so
 *      a 4xx produced no panel swap and no error — the row simply stayed
 *      put. Non-OK responses now fall through to the href fallback.
 *
 * 2026-08-29 — ORDER-DATA INTEGRITY. Four further defects addressed,
 * after two live orders showed routing properties landing on some lines
 * and not others:
 *   d) LINE-KEY TARGETING. Writes used /cart/change.js with id: <variant
 *      id>, which Shopify resolves to the FIRST line carrying that
 *      variant. With the same variant on two lines — a lay-buy line and
 *      a normal one, or two lines whose properties differ — the write
 *      landed on the wrong line and the intended line got nothing. Order
 *      Jokers67500OS showed exactly this: three of four lines flagged,
 *      one bare. Writes now target the exact line key, re-resolved from
 *      /cart.js on retry because a successful write changes that key.
 *   e) A failed write was never retried. flaggedSig was set BEFORE the
 *      writes ran, so once a 409 exhausted its retries the flags stayed
 *      missing for that cart until its contents changed. The signature is
 *      now cleared on failure so the next availability pass re-attempts.
 *   f) Nothing waited for in-flight writes before checkout submitted.
 *      Navigation cancels an in-flight XHR, so a customer clicking Check
 *      out a second after opening the drawer could leave without their
 *      routing properties. The button is now held disabled while a write
 *      is outstanding — capped at 5s so a hung request can never block a
 *      sale. Deliberately NOT done by intercepting the click: the scan
 *      reminder already intercepts there and a second interceptor on that
 *      path has broken checkout before.
 *   g) selfWrite was cleared on a setTimeout(0) macrotask, but
 *      MutationObserver delivers its batch as a microtask — so a ShipZip
 *      toggle arriving in the same task as our own write was swallowed,
 *      p.shipzip never flipped true, and the button stayed disabled with
 *      "Please select a Delivery Option" after one had been selected.
 *      Replaced with per-button intent tracking, which has no timing
 *      component at all.
 *
 * 2026-08-29 — THE WRITER HAD NEVER WORKED. SBZ returns
 * unavailable_variant_ids as strings; /cart.js returns id as a number; every
 * match between them used === and was therefore always false. No line was ever
 * found, the queue was always empty, and no write was ever sent. That is why
 * _ml was absent from every order examined and why ShipZip's Premium rate —
 * correctly gated on 'Properties / Does not equal / ml=no / ALL products must
 * satisfy' — kept being offered on carts Rocklea could not fulfil. Fixed with
 * sameId(). Verified against the live SBZ response for variant 22223831859279.
 *
 * The same change removed _odm from the payload. It was a hardcoded 'Shipping'
 * that had never landed; repairing the id match would have started it
 * overwriting ShipZip's correct Local Delivery / Store Pickup values.
 *
 * 2026-09-04 — "SHIPPING NOT AVAILABLE" AT CHECKOUT. Regression from the
 * 2026-08-29 change above. /cart/change.js with properties: REPLACES the
 * line's whole property set. The _ml writer built that set from the /cart.js
 * snapshot taken during the availability pass, so when ShipZip had written
 * _odm/_odd/_odate/_odt/_LocationId to the line in the meantime (it does so
 * when the customer selects a delivery option), our write threw them away.
 * ShipZip's rates carry a per-product Delivery Type condition; a line with no
 * _odm matches no rate, Shopify receives zero rates and shows "Shipping not
 * available". Every abandoned checkout with the error had ml=no lines missing
 * _odm. Before 08-29 the writer never landed, so it never clobbered either.
 * Fix: read the line FRESH immediately before every write and merge _ml onto
 * the properties ShipZip has actually put there. See runQueue().
 *
 * 2026-09-05 — ROUTING PROPAGATION. ShipZip stamps its routing set on the
 * lines present when the customer picks a tile and never revisits, so a line
 * added afterwards has no _odm. Abandoned checkout #32622498775211: line 1
 * "_odm: Shipping" (customer chose Standard), line 2 bare — and the bare line
 * unlocked a $77 Premium rate, because "Delivery Type does not equal Store
 * Pickup / Shipping" is passed by a line with no Delivery Type at all. The
 * availability pass now copies a stamped sibling's ShipZip set onto any bare
 * line (fresh-read, never invented). Queue entries are now one per LINE with
 * a list of mutations, so a line needing both this and _ml gets one write.
 *
 * 2026-09-25 — DUPLICATE LINES, QUANTITY LOSS, PICKUP RESET, BACK BUTTON.
 *   h) The same variant could sit on two lines (a re-add lands bare while the
 *      first line already carries routing/_ml). Worse, when a property write
 *      made a line IDENTICAL to another, Shopify collapsed the two and kept
 *      only the quantity sent in that write — the other line's units vanished.
 *      Now: (1) property writes never make two lines identical — a write that
 *      would is skipped; (2) duplicate lines (same variant, same non-routing
 *      properties) are CONSOLIDATED with quantity-only writes: the keeper (the
 *      routed line) is raised to the combined quantity, verified, and only
 *      then are the others removed. No property is ever written by this step,
 *      so _odm can't be lost. If Shopify caps the quantity (stock), the keeper
 *      is put back and the lines are left alone.
 *   i) _ml is now written to EVERY line of a flagged variant, not just the
 *      first — otherwise two lines of one variant could never match.
 *   j) Store Pickup reset: if the store the customer chose drops out of
 *      availability (cart grew past its stock), the pickup stamp is reset to
 *      the Standard default, ShipZip is re-rendered with no selection and
 *      checkout is held with a message until an option is picked again.
 *   k) Back from checkout restores the page from the browser cache with stale
 *      line keys ("no valid id or line parameter" on +/-). Reload instead.
 *   l) Section 6: a re-add of the same size merges into the existing line at
 *      add time. ShipZip's own per-line write (by position, with its own
 *      quantity) was collapsing duplicate lines and losing units. The same
 *      section re-resolves a stale line key on the drawer's +/- buttons.
 *   m) Routing guard: after ShipZip's own per-line rewrites (which can strip
 *      _odm after a quantity change), any line left without _odm is restamped.
 *   n) 2026-10-11 MIXED DELIVERY FIX. Order Jokers68928OS: line 1 Local
 *      Delivery 15/10 (Express chosen, Type Of Order = Local Delivery), line 2
 *      (a Frequently-Bought-Together add) "_odm: Shipping" with no date. Same
 *      shape on abandoned checkouts (Pickup + Shipping). The line was not bare,
 *      so copyRouting never touched it. Now: when the customer's choice is
 *      certain (active ShipZip tab AND Type Of Order agree; if no tab is drawn,
 *      Type Of Order alone, Express/Pickup only) and one line carries that
 *      choice complete, every line with a DIFFERENT _odm gets that line's set.
 *      Runs in the availability pass, the routing guard, ~2.5s after any
 *      /cart/add (alignment ONLY there - bare lines are left to ShipZip and
 *      the existing passes, exactly as before), and (JokersCart.alignNow) on
 *      the checkout click.
 * ------------------------------------------------------------------ */

/* Shared state between the two blocks below. Flag writes and removals
   both POST to /cart/change.js; they must never overlap. */
window.JokersCart = window.JokersCart || { writes: 0, removing: false, writeStart: 0 };

/* ---------- 1-4: availability, routing flags, checkout gating ---------- */
(function () {
  var LOCATION_IDS = [35466936399, 14005338191, 45235206, 42986502, 76133531819];
  var ROCKLEA_ID = "45235206";
  var PICKUP_MSG = 'Store Pick-Up is not available as not all of your items are available at our store.';
  var PREMIUM_MSG = 'Our Premium service is not available as not all of your items are available from our Rocklea warehouse. You will only have the Express option available.';
  /* How long the checkout button may be held for an in-flight routing-flag
     write. A hung request must never cost a sale, so past this the button is
     released and the order goes through without the flags. */
  var WRITE_HOLD_MS = 5000;
  /* Two independent checkout surfaces — the cart drawer and the /cart page.
     Each has its own form, checkout button and Conditions of Sale box, and
     each gates on its own pair. The old code read the checkbox with a single
     document-wide querySelector, so the moment a box existed on the cart page
     the drawer would start reading the PAGE's box instead of its own.

     On the cart page BOTH the button and the checkbox sit OUTSIDE
     <form id="cart"> and are associated by the form="" attribute, so
     descendant selectors never reach them — match on [form="cart"]. */
  var PAIRS = [
    {
      btn: 'form#cartDrawerForm [data-checkout], form#cartDrawerForm button[name="checkout"]',
      cb: 'input[id^="CartFormInput-"]:not([form="cart"])',
      msgId: 'jokers-terms-message',
      shipzip: false
    },
    {
      btn: '[data-checkout][form="cart"], button[name="checkout"][form="cart"]',
      cb: 'input[id^="CartFormInput-"][form="cart"]',
      msgId: 'jokers-terms-message-cart',
      shipzip: false,
      /* ShipZip prints its own delivery-option prompt inside the drawer but
         not on the cart page, so this surface has to say it itself. */
      deliveryMsg: true,
      /* Sits above the Conditions of Sale line on this surface — below it the
         prompt lands between the checkbox and Check out and reads as if it
         belongs to the button. */
      msgBefore: true
    }
  ];
  var CB = 'input[id^="CartFormInput-"]';   /* change-event filter only */
  var MSG_TEXT = 'Please accept the Conditions of Sale to proceed.';
  var MSG_DELIVERY = 'Please select a Delivery Option to proceed.';
  var MSG_PICKUP_RESET = 'Your chosen pickup store no longer has enough stock for this order. Please select a Delivery Option again.';
  var resetMsg = null;
  var consolidatedSig = null;
  var JK = window.JokersCart;

  var running = false, done = false, hideIds = [], rockleaOk = true;
  var tab2Default = null, reinitTimer = null, writeCount = 0;
  var dataSig = null, flaggedSig = null, flagFailed = false;
  var watched = [], tick = null;
  var shipzipTries = 0, shipzipWarned = false;

  /* ShipZip runs jQuery.noConflict(true) on one load path, which deletes
     the global $. Always resolve through here. */
  function jq() {
    if (typeof csJq !== 'undefined' && csJq) { return csJq; }
    return window.jQuery || null;
  }
  function sig(cart) {
    return cart.items.map(function (i) { return i.id + ':' + i.quantity; }).join(',');
  }

  /* Nothing below needs to run for an empty cart. Re-initialising ShipZip
     or re-checking availability with no line items is pure latency —
     it's what made removing the LAST item feel slow. */
  function cartHasItems() {
    return !!document.querySelector('#CartDrawer li[id^="CartDrawer-Item-"]');
  }

  /* ---------- availability display ---------- */
  function applyHides() {
    var $j = jq();
    if (!$j || !hideIds.length) { return; }
    hideIds.forEach(function (id) {
      $j('div#cs-tab3 .cs-radio-card[data-locatioid="' + id + '"]').hide();
      $j('#cs-location-list .cs-radio-card[data-locatioid="' + id + '"]').hide();
    });
  }

  function evaluatePickup() {
    var $j = jq();
    if (!$j || !done) { return; }
    applyHides();
    var cards = document.querySelectorAll('#cs-location-list .cs-radio-card, div#cs-tab3 .cs-radio-card');
    if (!cards.length) { return; }
    var anyVisible = Array.prototype.some.call(cards, function (el) { return el.style.display !== 'none'; });
    if (anyVisible) {
      $j('div#cs-tab3 .sp-message').remove();
      $j('div#cs-tab3 .tab-inner-content').show();
    } else {
      if ($j('div#cs-tab3 .sp-message').length === 0) {
        $j('div#cs-tab3').prepend('<p class="sp-message">' + PICKUP_MSG + '</p>');
      }
      $j('div#cs-tab3 .tab-inner-content').hide();
    }
  }

  function evaluatePremium() {
    var $j = jq();
    if (!$j || !done) { return; }
    var $p = $j('div#cs-tab2 .tab-inner-content > p').first();
    if (!$p.length) { return; }
    if (tab2Default === null && $p.html() !== PREMIUM_MSG) { tab2Default = $p.html(); }
    if (!rockleaOk) {
      if ($p.html() !== PREMIUM_MSG) { $p.html(PREMIUM_MSG); }
    } else if (tab2Default !== null && $p.html() === PREMIUM_MSG) {
      $p.html(tab2Default);
    }
  }

  /* ---------- fulfilment routing flags ---------- */
  /* SBZ returns unavailable_variant_ids as STRINGS ('22223831859279') while
     /cart.js returns id as a NUMBER (22223831859279). Every match between the
     two was done with ===, which is false for those, so no line was ever found,
     the write queue stayed empty and NOTHING was ever written. That is why _ml
     has been absent from every order and Premium was never suppressed. Compare
     as strings everywhere the two sources meet. 2026-08-29. */
  function sameId(a, b) { return String(a) === String(b); }

  function needsFlags(cart, variantId) {
    /* Returns true when the line's _ml does not match what Rocklea stock says
       it should be — in EITHER direction. Setting the flag was never the whole
       job: nothing removed it, so a cart that once exceeded Rocklea stock kept
       ml=no after the quantity came back down, and ShipZip went on withholding
       Premium forever. Verified 2026-08-29: cart reduced 3 → 2, flag persisted,
       Premium stayed hidden. */
    var line = cart.items.find(function (i) { return sameId(i.id, variantId); });
    var p = (line && line.properties) || {};
    var has = p['_ml'] === 'ml=no';
    return rockleaOk ? has : !has;
  }

  /* ---------- routing-property propagation (2026-09-05) ---------- */
  /* ShipZip stamps _odm/_odd/_odate/_odt/_LocationId onto the lines that exist
     at the moment the customer picks a delivery tile, and never revisits. A
     line added AFTER that (same product again from a product page, a second
     item) lands with no properties at all. Abandoned checkout #32622498775211
     (West Perth): line 1 "_odm: Shipping", line 2 bare — and the bare line
     unlocked a $77 Premium rate, because the Express/Premium rate condition
     is Delivery Type DOES NOT EQUAL Store Pickup / Shipping, which a line
     with no _odm passes trivially.
     Fix: when a pass finds a stamped sibling and a bare line, copy the
     sibling's ShipZip set onto the bare line. This propagates a choice the
     customer already made; it never invents one — no stamped sibling, no
     write. The values are taken from the FRESH read at write time, so a tile
     switch between the pass and the write is honoured. */
  var ROUTING_KEYS = ['_odm', '_odd', '_odate', '_odt', '_LocationId'];

  function hasRouting(item) {
    return !!(item && item.properties && item.properties['_odm']);
  }

  function copyRouting(props, fresh) {
    var donor = fresh.items.find(hasRouting);
    if (!donor) { return; }
    ROUTING_KEYS.forEach(function (k) {
      if (k in donor.properties) { props[k] = donor.properties[k]; }
    });
  }
  /* ---------- default routing for a cart with NO stamped line (2026-09-10) ---------- */
  /* copyRouting above only propagates a choice the customer already made. It
     cannot help a cart where NOTHING is stamped: a single line added by an app
     (Zipchat add-to-cart), the product-page Buy-it-now button, or a saved
     /checkout link, then straight to checkout without the drawer tiles ever
     running. Abandoned checkout #32664044961963 (Harley Quinn M, 2026-09-10):
     one line, only _ml, no _odm — and it was offered a $30 Premium rate,
     because the Express/Premium condition "Delivery Type does not equal Store
     Pickup / Shipping" is passed by a line with no Delivery Type at all.
     Fix: when the fresh read shows no routed line anywhere in the cart, stamp
     _odm: Shipping (the Standard rate's Delivery Type) on the bare line. That
     is the only value that can never unlock Express/Premium. It is a DEFAULT,
     not a choice: ShipZip overwrites _odm the moment the customer picks any
     tile in the drawer, and the fresh read means a tile chosen between the
     pass and the write is honoured, never clobbered. */
  function defaultRouting(props, fresh) {
    if (props['_odm']) { return; }
    if (fresh.items.some(hasRouting)) { copyRouting(props, fresh); return; }
    /* 2026-10-02: a quantity change makes ShipZip rewrite the line WITHOUT its
       delivery props, so a single-line cart has no donor. The customer's choice
       still lives in the CART ATTRIBUTES ShipZip wrote (Type Of Order, Delivery
       Date/Day/Time, LocationId) - rebuild the line's routing from those instead
       of falling back to Standard, which silently dropped Express/Premium. */
    var a = fresh.attributes || {};
    /* ...but never while the Standard tab is the live selection: the attribute
       can be stale from an earlier choice and would resurrect Express. */
    var stdLive = !!document.querySelector('#csShippingAppCode li.cs-tab_last[rel="cs-tab1"].active');
    if (!stdLive && a['Type Of Order'] === 'Local Delivery' && a['Delivery Date']) {
      props['_odm'] = 'Local Delivery';
      props['_odate'] = a['Delivery Date'];
      if (a['Delivery Day']) { props['_odd'] = a['Delivery Day']; }
      if (a['Delivery Time']) { props['_odt'] = a['Delivery Time']; }
      if (a['LocationId']) { props['_LocationId'] = a['LocationId']; }
      return;
    }
    /* Same for Store Pickup (2026-10-02): store id is 'LocationId'; the pickup
       date/day/time attribute names come from ShipZip's settings text, so find
       them by pattern rather than a fixed key. */
    if (!stdLive && a['Type Of Order'] === 'Store Pickup' && a['LocationId']) {
      var pick = function (re) { for (var k in a) { if (re.test(k) && a[k]) { return a[k]; } } return ''; };
      props['_odm'] = 'Store Pickup';
      props['_LocationId'] = a['LocationId'];
      var pdt = pick(/pick.?up.*date/i), pdy = pick(/pick.?up.*day/i), ptm = pick(/pick.?up.*time/i);
      if (pdt) { props['_odate'] = pdt; }
      if (pdy) { props['_odd'] = pdy; }
      if (ptm) { props['_odt'] = ptm; }
      return;
    }
    props['_odm'] = 'Shipping';
  }

  /* ---------- delivery alignment (2026-10-11, see header n) ---------- */
  var TAB_ODM = { 'cs-tab1': 'Shipping', 'cs-tab2': 'Local Delivery', 'cs-tab3': 'Store Pickup' };
  function chosenMethod(cart) {
    var a = String(((cart && cart.attributes) || {})['Type Of Order'] || '');
    if (!a) { return ''; }
    var act = document.querySelector('#csShippingAppCode li.cs-tab_last.active');
    if (act) {
      var m = TAB_ODM[act.getAttribute('rel')] || '';
      return m && m === a ? m : '';
    }
    /* No tab showing (widget not drawn, or mid re-render): only an Express or
       Pickup choice is propagated - Standard is std-default.js's job. */
    return (a === 'Local Delivery' || a === 'Store Pickup') ? a : '';
  }
  function completeFor(p, m) {
    if (!p || p['_odm'] !== m) { return false; }
    if (m === 'Local Delivery') { return !!p['_odate']; }
    if (m === 'Store Pickup') { return !!p['_LocationId']; }
    return true;
  }
  function misrouted(cart) {
    if (!cart || !cart.items) { return []; }
    var m = chosenMethod(cart);
    if (!m) { return []; }
    if (!cart.items.some(function (i) { return completeFor(i.properties, m); })) { return []; }
    return cart.items.filter(function (i) { return i.key && hasRouting(i) && i.properties['_odm'] !== m; });
  }
  function alignRouting(props, fresh) {
    var m = chosenMethod(fresh);
    if (!m || props['_odm'] === m) { return; }
    var donor = fresh.items.find(function (i) { return completeFor(i.properties, m); });
    if (!donor) { return; }
    /* copy the donor's set exactly - a key the donor lacks is dropped, so the
       duplicate-line (twin) check in runQueue compares like with like */
    ROUTING_KEYS.forEach(function (k) {
      if (k in donor.properties) { props[k] = donor.properties[k]; } else { delete props[k]; }
    });
  }

  function setMl(props) {
    /* Shopify removes a property when it is sent as an empty string. */
    props['_ml'] = rockleaOk ? '' : 'ml=no';
  }

  /* ---------- duplicate-line consolidation (2026-09-25) ---------- */
  /* Lines are duplicates when they share a variant AND the same properties
     other than routing/_ml (so two different Size Scan results stay apart). */
  function coreKey(item) {
    var p = item.properties || {}, o = {};
    Object.keys(p).sort().forEach(function (k) {
      if (ROUTING_KEYS.indexOf(k) !== -1 || k === '_ml') { return; }
      if (p[k] === '' || p[k] === null || p[k] === undefined) { return; }
      o[k] = String(p[k]);
    });
    return String(item.id) + '|' + JSON.stringify(o);
  }
  function dupGroups(cart) {
    var map = {}, order = [], groups = [];
    (cart.items || []).forEach(function (i) {
      if (!i.key) { return; }
      var k = coreKey(i);
      if (!map[k]) { map[k] = []; order.push(k); }
      map[k].push(i);
    });
    order.forEach(function (k) { if (map[k].length > 1) { groups.push(map[k]); } });
    return groups;
  }
  /* Keep the line that already carries the customer's routing choice. */
  function pickKeeper(lines) {
    function score(l) { return (hasRouting(l) ? 100 : 0) + Object.keys(l.properties || {}).length; }
    var best = lines[0];
    lines.forEach(function (l) { if (score(l) > score(best)) { best = l; } });
    return best;
  }
  /* Quantity-only writes: raise the keeper to the combined quantity, CHECK
     Shopify actually gave it that quantity, and only then remove the others.
     If it was capped (not enough stock), put the keeper back and leave the
     lines as they are — never lose units.
     Runs ALONE: it waits for any in-flight write to finish, and the flags
     pass is blocked while it runs. Every removal re-reads the cart first and
     re-finds the duplicate by its group, never by a remembered key — a write
     in between changes line keys, and a stale key 400s, which left an extra
     unit in the cart (seen in preview 2026-09-25). */
  var consolidating = false;
  function consolidate(done) {
    var $j = jq();
    if (!$j || JK.removing || consolidating) { done(false); return; }
    consolidating = true;
    var changed = false;
    function post(data, ok, fail, tries) {
      tries = tries || 0;
      $j.ajax({
        url: "/cart/change.js?csapp=shipcs", type: "post", dataType: "json", data: data,
        success: ok,
        error: function (xhr) {
          var st = xhr && xhr.status;
          if (st === 409 && tries < 2) { setTimeout(function () { post(data, ok, fail, tries + 1); }, 400); return; }
          fail(st);
        }
      });
    }
    function begin() {
      writeCount++;
      JK.writes++;
      if (JK.writes === 1) { JK.writeStart = Date.now(); }
      enforce();
      setTimeout(enforce, WRITE_HOLD_MS + 100);
      function end() {
        writeCount--;
        if (JK.writes > 0) { JK.writes--; }
        consolidating = false;
        enforce();
        done(changed);
      }
      $j.getJSON('/cart.js?csapp=shipcs', function (fresh) {
        var groups = dupGroups(fresh).map(function (lines) {
          var keeper = pickKeeper(lines);
          return {
            ck: coreKey(keeper),
            keeperKey: keeper.key,
            keeperQty: keeper.quantity,
            total: lines.reduce(function (s, l) { return s + l.quantity; }, 0)
          };
        });
        function nextGroup() {
          if (JK.removing || !groups.length) { end(); return; }
          var g = groups.shift();
          post({ id: g.keeperKey, quantity: g.total }, function (cart) {
            var k = cart && cart.items && cart.items.find(function (i) { return i.key === g.keeperKey; });
            if (!k || k.quantity !== g.total) {
              /* capped — restore the keeper and leave this group alone */
              post({ id: g.keeperKey, quantity: g.keeperQty }, nextGroup, nextGroup);
              return;
            }
            changed = true;
            removeRest(g, 0);
          }, function (st) {
            console.error('[jokers-cart] could not combine duplicate lines (status ' + st + ')');
            nextGroup();
          });
        }
        /* Re-read, find any line in this group other than the keeper, remove
           it by its CURRENT key; repeat until only the keeper is left. */
        function removeRest(g, attempts) {
          if (attempts > 6) {
            flagFailed = true;
            console.error('[jokers-cart] could not remove duplicate line after retries');
            nextGroup();
            return;
          }
          $j.getJSON('/cart.js?csapp=shipcs', function (now) {
            /* Safety: only remove while the keeper is still there, unchanged,
               holding the combined quantity. If something else rewrote it,
               stop — two lines is a cosmetic problem, a lost unit is not. */
            var keeperNow = now.items.find(function (i) { return i.key === g.keeperKey; });
            if (!keeperNow || keeperNow.quantity !== g.total) {
              console.error('[jokers-cart] merge stopped: keeper line changed underneath');
              nextGroup();
              return;
            }
            var extra = now.items.find(function (i) { return i.key !== g.keeperKey && coreKey(i) === g.ck; });
            if (!extra) { nextGroup(); return; }
            post({ id: extra.key, quantity: 0 }, function () { removeRest(g, 0); }, function (st) {
              console.error('[jokers-cart] duplicate-line removal failed (status ' + st + '), retrying');
              setTimeout(function () { removeRest(g, attempts + 1); }, 300);
            });
          }).fail(function () { setTimeout(function () { removeRest(g, attempts + 1); }, 300); });
        }
        nextGroup();
      }).fail(end);
    }
    /* Wait for any in-flight flag write to finish first (cap ~8s). */
    (function waitIdle(tries) {
      if (JK.writes === 0 || tries > 160) { begin(); return; }
      setTimeout(function () { waitIdle(tries + 1); }, 50);
    })(0);
  }

  /* ---------- pickup dropout (2026-09-25) ---------- */
  /* If the store the customer picked for pickup can no longer fulfil the
     order, reset the lines to the Standard default (_odm: Shipping — the one
     value that can never unlock another rate), blank the pickup details,
     re-render ShipZip with no selection and hold checkout until an option is
     picked again. Fails safe: no LOCATION_IDS match in _LocationId → no-op. */
  function isPickup(p) {
    return !!(p && p['_odm'] && /pick/i.test(p['_odm']));
  }
  /* ShipZip stores its OWN location number in _LocationId (Southport =
     65573); the stock check uses Shopify location ids. Map one to the other
     (read from ShipZip's pickup tiles, 2026-09-25), with a live lookup on the
     tiles as a fallback for any store added later. */
  var PICKUP_MAP = { '21709': '14005338191', '21710': '45235206', '65573': '76133531819' };
  function pickupLoc(p) {
    var v = String(p['_LocationId'] || '').replace(/\D/g, '');
    if (!v) { return ''; }
    if (PICKUP_MAP[v]) { return PICKUP_MAP[v]; }
    var card = document.querySelector('.cs-radio-card[data-location-mainid="' + v + '"]');
    return card ? String(card.getAttribute('data-locatioid') || '') : v;
  }
  function pickupDropped(p) {
    if (!isPickup(p)) { return false; }
    var loc = pickupLoc(p);
    return !!loc && hideIds.some(function (h) { return String(h) === loc; });
  }
  function clearPickup(props) {
    if (!pickupDropped(props)) { return; }
    props['_odm'] = 'Shipping';
    ['_odd', '_odate', '_odt', '_LocationId'].forEach(function (k) {
      if (k in props) { props[k] = ''; }
    });
  }
  function resetDelivery() {
    resetMsg = MSG_PICKUP_RESET;
    PAIRS.forEach(function (p) { p.shipzip = false; });
    var mount = document.getElementById('csShippingAppCode');
    if (mount) { mount.innerHTML = ''; }   /* sync() re-inits ShipZip with no selection */
    enforce();
    schedule();
  }

  /* One queue entry per LINE, keyed on line key, carrying every mutation
     that line needs. Two separate entries for one line would mean two
     writes, and the first changes the line key — the second would then
     fall back to a variant-id lookup, which is exactly wrong on a cart
     where the same variant sits on two lines. */
  function addEntry(queue, line, apply) {
    var e = queue.find(function (q) { return q.key === line.key; });
    if (!e) { e = { id: String(line.id), key: line.key, applies: [] }; queue.push(e); }
    e.applies.push(apply);
  }

  function applyCartFlags(cart, data, cart_data) {
    var $j = jq();
    if (!$j) { return; }
    /* Never write while a removal is running — the line we are about to
       change may be the one being deleted. Nor while duplicate lines are
       being merged (their keys are about to change). */
    if (JK.removing || consolidating) { return; }
    var queue = [];

/* 1. Routing — independent of what SBZ said. A stamped sibling is copied;
          with no stamped line anywhere, bare lines get the Standard default
          (defaultRouting re-checks for a donor on the fresh read). */
    if (cart.items.some(hasRouting)) {
      cart.items.forEach(function (i) {
        if (i.key && !hasRouting(i)) { addEntry(queue, i, copyRouting); }
      });
    } else {
      cart.items.forEach(function (i) {
        if (i.key && !hasRouting(i)) { addEntry(queue, i, defaultRouting); }
      });
    }
    /* 1b. (2026-10-11) a line carrying a DIFFERENT method from the customer's choice */
    misrouted(cart).forEach(function (i) { addEntry(queue, i, alignRouting); });

    /* 2. _ml flag. */
    var allUnavail = data.every(function (d) { return d.unavailable_variant_ids && d.unavailable_variant_ids.length > 0; });

    /* When every location reports full availability there is nothing to FLAG,
       but there may still be a stale ml=no to CLEAR — so this can no longer
       return early. It used to, which is why reducing a cart below the Rocklea
       threshold left the flag in place and Premium permanently withheld.
       Sweep every line that carries the flag and drop it. */
    if (data.every(function (d) { return !d.unavailable_variant_ids || d.unavailable_variant_ids.length === 0; })) {
      cart.items.forEach(function (i) {
        if (i.properties && i.properties['_ml'] === 'ml=no' && i.key) { addEntry(queue, i, setMl); }
      });
    } else {
      /* Collect the variants needing flags FIRST. This used to fire one
         write per LOCATION, and the five locations normally name the same
         variant — so a single availability pass sent up to five concurrent
         /cart/change.js calls against one line (14 of them were observed
         for a two-item cart). needsFlags() reads a stale snapshot, so every
         duplicate passed the guard. Dedupe, then write one at a time. */
      var targets = [];
      function addTarget(id) {
        if (id === null || id === undefined) { return; }
        var s = String(id);
        if (targets.indexOf(s) === -1) { targets.push(s); }
      }

      if (allUnavail) {
        var first = data[0];
        if (first && first.unavailable_variant_ids && first.unavailable_variant_ids.length) {
          addTarget(first.unavailable_variant_ids[0]);
        }
      } else {
        data.forEach(function (d) {
          if (d.unavailable_variant_ids && d.unavailable_variant_ids.length) {
            addTarget(d.unavailable_variant_ids[0]);
          }
        });
      }

      /* Resolve each target to its exact LINE KEY. Targeting by variant id
         makes Shopify pick the first line carrying that variant, which is the
         wrong line whenever the same variant appears twice — and the intended
         line then receives nothing at all. */
      targets.forEach(function (id) {
        /* Every line of the variant, not just the first (2026-09-25). */
        cart.items.forEach(function (line) {
          if (!line.key || !sameId(line.id, id)) { return; }
          var has = !!(line.properties && line.properties['_ml'] === 'ml=no');
          if (rockleaOk ? has : !has) { addEntry(queue, line, setMl); }
        });
      });
    }

    /* 3. Pickup dropout — the chosen store can no longer fulfil the order. */
    if (cart.items.some(function (i) { return pickupDropped(i.properties); })) {
      cart.items.forEach(function (i) { if (i.key) { addEntry(queue, i, clearPickup); } });
      resetDelivery();
    }

    if (!queue.length) { return; }
    runQueue(queue);
  }

  /* Serialised writer. Each entry is one line; its applies[] run in order
     against the line's FRESH properties, then one write is sent. */
  function runQueue(queue) {
    var $j = jq();
    if (!$j) { return; }
    var retries = {};

    /* The whole chain counts as ONE outstanding write, so a removal
       waiting on JK.writes waits for all of it, not just the first call. */
    writeCount++;
    JK.writes++;
    if (JK.writes === 1) { JK.writeStart = Date.now(); }
    /* Hold the checkout button for the duration, and release it again when
       the hold expires even if the chain never finishes. */
    enforce();
    setTimeout(enforce, WRITE_HOLD_MS + 100);

    function finished() {
      writeCount--;
      if (JK.writes > 0) { JK.writes--; }
      if (writeCount === 0 && !JK.removing) {
        /* Let a later availability pass re-attempt anything that failed —
           flaggedSig is set optimistically before the writes run. */
        if (flagFailed) { flaggedSig = null; flagFailed = false; }
             }
      enforce();
    }

    function sameProps(a, b) {
      var ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) { return false; }
      return ka.every(function (k) { return b.hasOwnProperty(k) && a[k] === b[k]; });
    }

    function next() {
      if (JK.removing || !queue.length) { finished(); return; }
      var target = queue.shift();
      var variantId = target.id;
      /* 2026-09-04 — READ FRESH BEFORE EVERY WRITE. /cart/change.js REPLACES the
         line's whole property set. Building it from the availability-pass
         snapshot meant that when ShipZip had since written _odm/_odd/_odate/
         _odt/_LocationId to the line, our write threw them away. ShipZip's
         rates are conditioned on Delivery Type per product, so a line with no
         _odm matches no rate → "Shipping not available" at checkout. Every
         abandoned checkout showing that error had ml=no lines missing _odm.
         The fresh line supplies key, quantity and properties; applies[] add
         only what this file owns: _ml, and (2026-09-05) a copy of a sibling's
         ShipZip routing set onto a line that has none.

         _odm is never INVENTED here. It carries the fulfilment method —
         Store Pickup / Local Delivery / Shipping — which only ShipZip knows.
         copyRouting only replicates a value ShipZip already wrote to another
         line of the same cart. */
      $j.getJSON('/cart.js?csapp=shipcs', function (fresh) {
        var line = fresh.items.find(function (i) { return i.key === target.key; }) ||
                   fresh.items.find(function (i) { return sameId(i.id, variantId); });
        if (!line || !line.key) { next(); return; }
        var before = line.properties || {};
        var props = Object.assign({}, before);
        target.applies.forEach(function (fn) { fn(props, fresh); });
        /* ShipZip may have got there first (it stamps on tile selection).
           Nothing to change → no write, no key churn. */
        if (sameProps(props, before)) { next(); return; }
        /* 2026-09-25 — never let a property write make this line IDENTICAL to
           another line: Shopify would collapse them and keep only this write's
           quantity. Skip it; the consolidation pass merges them safely. */
        var twin = fresh.items.find(function (i) {
          return i.key !== line.key && sameId(i.id, line.id) && sameProps(i.properties || {}, props);
        });
        if (twin) { next(); return; }
        $j.ajax({
          url: "/cart/change.js?csapp=shipcs", type: "post", dataType: "json",
          data: { id: line.key, quantity: line.quantity, properties: props },
          success: function () { next(); },
          error: function (xhr) {
            var status = xhr && xhr.status;
            if ((status === 409 || status === 404 || status === 422) && (retries[target.key] || 0) < 2) {
              retries[target.key] = (retries[target.key] || 0) + 1;
              /* The key is re-resolved by the fresh read on the retry, so a
                 write that changed it (ours or ShipZip's) cannot 404 forever. */
              queue.unshift(target);
              setTimeout(next, 400);
              return;
            }
            flagFailed = true;
            console.error('[jokers-cart] line-property write failed for variant ' +
                          variantId + ' (status ' + status + ')');
            next();
          }
        });
      }).fail(function () { flagFailed = true; next(); });
    }

    next();
  }

  function fetchAvailability(force) {
    var $j = jq();
    if (!$j || running || JK.removing || consolidating) { return; }
    running = true;
    $j.getJSON('/cart.js?csapp=shipcs', function (cart) {
      if (!cart.items || cart.items.length === 0) { running = false; return; }
      var thisSig = sig(cart);
      if (!force && done && dataSig === thisSig) { running = false; schedule(); return; }
      var cart_data = cart.items.map(function (i) { return { id: i.id, qty: i.quantity }; });
      $j.ajax({
        url: "https://sbz.cirkleinc.com/get-variant-location-availability",
        type: "post", dataType: "JSON", crossDomain: true,
        data: { location_ids: LOCATION_IDS, cart_data: cart_data, shop: Shopify.shop },
        success: function (r) {
          var data = (r && r.unavailable_data) || [];
          hideIds = data.filter(function (d) { return d.unavailable_variant_ids && d.unavailable_variant_ids.length; })
                        .map(function (d) { return d.location_id; });
          var rk = data.find(function (d) { return String(d.location_id) === ROCKLEA_ID; });
          rockleaOk = !rk || !rk.unavailable_variant_ids || rk.unavailable_variant_ids.length === 0;
          done = true; running = false; dataSig = thisSig;
          /* Merge duplicate lines first; the flags pass then runs on the
             consolidated cart (cart:refresh → onCartEvent → fresh pass). */
          if (!JK.removing && consolidatedSig !== thisSig && dupGroups(cart).length) {
            consolidatedSig = thisSig;
            consolidate(function (changed) {
              if (changed) {
                dataSig = null; flaggedSig = null;
                document.dispatchEvent(new CustomEvent('cart:refresh', { detail: {} }));
              } else if (flaggedSig !== thisSig) {
                flaggedSig = thisSig; applyCartFlags(cart, data, cart_data);
              }
            });
            schedule();
            return;
          }
          if (flaggedSig !== thisSig) { flaggedSig = thisSig; applyCartFlags(cart, data, cart_data); }
          schedule();
        },
        error: function () { running = false; }
      });
    });
  }

  /* ---------- checkout gating ---------- */
  function writesBusy() {
    return JK.writes > 0 && (Date.now() - JK.writeStart) < WRITE_HOLD_MS;
  }

  function messageEl(p, cb) {
    var el = document.getElementById(p.msgId);
    if (el) { return el; }
    if (!cb) { return null; }
    var label = cb.closest('label') || cb.parentNode;
    if (!label || !label.parentNode) { return null; }
    el = document.createElement('div');
    el.id = p.msgId;
    el.className = 'grid';
    el.style.color = '#F49F11';
    el.style.display = 'none';
    /* Only the cart page renders this above the checkbox, where it needs
       breathing room; in the drawer it sits below and doesn't. */
    if (p.msgBefore) { el.style.marginBottom = '20px'; }
    el.innerHTML = '<span class="text-sm" data-jk-msg></span>';
    label.parentNode.insertBefore(el, p.msgBefore ? label : label.nextSibling);
    return el;
  }

  /* The cart page's Conditions of Sale box comes from the theme's stock
     custom_field block, which runs its label through Liquid's escape filter
     — so the block can only ever render plain text and the link to the terms
     is lost. Restore the drawer's exact wording and anchor once, on whatever
     text node the block produced. */
  var TERMS_URL = '/pages/terms-and-conditions-including-refund-policy';
  var TERMS_LEAD = 'I have read and agreed to the ';
  var TERMS_LINK = 'Conditions of Sale';
  var termsLabelDone = false;

  function upgradeTermsLabel() {
    if (termsLabelDone) { return; }
    var cb = document.querySelector('input[id^="CartFormInput-"][form="cart"]');
    if (!cb) { return; }
    var label = cb.closest('label');
    if (!label) { return; }
    /* Walk the label's own child nodes and replace only the text — never
       innerHTML on the label, which would destroy the input and its form
       association along with it. */
    var replaced = false;
    Array.prototype.slice.call(label.childNodes).forEach(function (n) {
      if (n.nodeType !== 3 || !n.nodeValue || !n.nodeValue.trim()) { return; }
      if (replaced) { n.nodeValue = ''; return; }
      var span = document.createElement('span');
      span.appendChild(document.createTextNode(TERMS_LEAD));
      var a = document.createElement('a');
      a.href = TERMS_URL;
      a.target = '_blank';
      a.rel = 'noopener';
      a.className = 'link';
      a.textContent = TERMS_LINK;
      /* Ticking the box is the label's job; clicking the link is not. */
      a.addEventListener('click', function (ev) { ev.stopPropagation(); });
      span.appendChild(a);
      label.replaceChild(span, n);
      replaced = true;
    });
    if (replaced) { termsLabelDone = true; }
  }

  function enforce() {
    var busy = writesBusy();
    PAIRS.forEach(function (p) {
      var btns = document.querySelectorAll(p.btn);
      if (!btns.length) { return; }
      var cb = document.querySelector(p.cb);
      var el = messageEl(p, cb);
      /* No Conditions of Sale box on this surface yet — never leave the
         button disabled with nothing the customer can tick to fix it. */
      if (!cb) { if (el) { el.style.display = 'none'; } return; }
      /* busy: a routing-flag write is still in flight. Submitting now would
         navigate away and cancel it, and the order would arrive without its
         _ml. Sub-second in practice, and hard-capped by WRITE_HOLD_MS. */
      var ok = p.shipzip && cb.checked && !busy;
      btns.forEach(function (b) {
        if (b.disabled !== !ok) {
          b.disabled = !ok;
          /* Record what WE set, so the observer below can tell our own
             writes from ShipZip's without relying on timing. */
          b.__jkLast = b.disabled;
        }
      });
      /* Two things can shut the button — no delivery option chosen (ShipZip
         holds it disabled) or the terms box unticked. Name whichever applies,
         rather than only ever mentioning the terms. Say nothing for the busy
         case: it clears on its own in well under a second, and a flashing
         message would read as an error. */
      if (el) {
        var span = el.querySelector('[data-jk-msg]');
        if (span) { span.textContent = (resetMsg && !p.shipzip) ? resetMsg : (p.shipzip ? MSG_TEXT : MSG_DELIVERY); }
        var show = !ok && !busy && (p.shipzip || p.deliveryMsg || !!resetMsg);
        el.style.display = show ? '' : 'none';
      }
    });
  }

  function watchButtons() {
    PAIRS.forEach(function (p) {
      document.querySelectorAll(p.btn).forEach(function (b) {
        if (watched.indexOf(b) !== -1) { return; }
        watched.push(b);
        if (!b.disabled) { p.shipzip = true; }
        b.__jkLast = b.disabled;
        /* Compare against what we last set rather than a selfWrite flag on a
           setTimeout(0). MutationObserver delivers its batch as a microtask,
           which runs BEFORE that timeout — so a ShipZip toggle arriving in the
           same task as our own write was being discarded, and p.shipzip never
           learned that a delivery option had been chosen. */
        new MutationObserver(function () {
          if (b.disabled === b.__jkLast) { return; }
          b.__jkLast = b.disabled;
          p.shipzip = !b.disabled; if (p.shipzip) { resetMsg = null; }   /* remember what ShipZip wanted; a re-pick clears the reset notice */
          enforce();
        }).observe(b, { attributes: true, attributeFilter: ['disabled'] });
      });
    });
  }

  /* ---------- one debounced pass ---------- */
  function reinitShipZip() {
    var $j = jq();
    var mount = document.getElementById('csShippingAppCode');
    if (!$j || !mount || mount.children.length > 0) { return; }
    if (!cartHasItems() || JK.removing) { return; }
    if (typeof MainCode !== 'function') {
      /* Warn once — sync() retries every 300ms and this would otherwise
         fill the console. */
      if (!shipzipWarned) {
        shipzipWarned = true;
        console.error('[jokers-cart] ShipZip MainCode not loaded — delivery options cannot render');
      }
      return;
    }
    /* Never swallow this. An empty catch here cost an evening on Aug 11:
       empty panel, clean console, and the app vendor concluded this file
       was the conflict. A thrown MainCode must be visible. */
    try {
      MainCode($j);
    } catch (e) {
      console.error('[jokers-cart] ShipZip MainCode threw', e);
    }
    fetchAvailability(false);
    /* MainCode can also return cleanly without populating the mount —
       which is what actually happened. Check, and say so. */
    shipzipTries++;
    setTimeout(function () {
      var m = document.getElementById('csShippingAppCode');
      if (m && m.children.length === 0 && cartHasItems() && shipzipTries >= 3) {
        console.error('[jokers-cart] ShipZip mount still empty after ' + shipzipTries + ' attempts');
      }
    }, 1000);
  }

  function sync() {
    tick = null;
    var mount = document.getElementById('csShippingAppCode');
    if (mount && mount.children.length === 0 && cartHasItems() && !JK.removing) {
      /* Arm ONCE rather than re-arming. This used to clearTimeout on every
         sync, so while drawer mutations arrived faster than 300ms the timer
         was cancelled each time and reinitShipZip never actually ran — the
         ShipZip mount stayed empty with no error anywhere. Nulling the handle
         inside the callback lets a later sync re-arm it, so if MainCode does
         fail the next pass retries instead of giving up. */
      if (!reinitTimer) {
        reinitTimer = setTimeout(function () {
          reinitTimer = null;
          reinitShipZip();
        }, 300);
      }
    }
    if (done) { evaluatePickup(); evaluatePremium(); }
    upgradeTermsLabel();
    watchButtons();
    enforce();
  }

  function schedule() { if (tick === null) { tick = setTimeout(sync, 150); } }

  /* Observe the drawer only — observing documentElement made the whole
     site sluggish. */
  var target = document.getElementById('CartDrawer') || document.body;
  new MutationObserver(schedule).observe(target, { childList: true, subtree: true });

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[aria-controls^="ShippingOption-"], [aria-controls="CartDrawer"]');
    if (t) { fetchAvailability(false); schedule(); }
  }, true);

  document.addEventListener('change', function (e) {
    if (e.target && e.target.matches && e.target.matches(CB)) { enforce(); }
  }, true);

  /* Trust the cart object carried on the event over the DOM — after a
     removal the panel may not have been swapped in yet, and an empty
     cart must never trigger another availability round trip. */
  function onCartEvent(e) {
    var c = e && e.detail && e.detail.cart;
    if (c && (!c.items || c.items.length === 0)) { return; }
    if (writeCount > 0 || JK.removing || !cartHasItems()) { return; }
    fetchAvailability(true);
  }
  document.addEventListener('cart:refresh', onCartEvent);
  document.addEventListener('cart:updated', onCartEvent);

  /* warm the data early so the panel is ready when opened */
  if (document.readyState === 'complete') {
    setTimeout(function () { fetchAvailability(false); }, 800);
  } else {
    window.addEventListener('load', function () {
      setTimeout(function () { fetchAvailability(false); }, 800);
    });
  }

  /* ---------- routing guard after ShipZip rewrites (2026-09-25) ----------
     Request log, preview: after a quantity change ShipZip rewrites each line
     by position (body "line=N&quantity=…") with only the non-delivery
     properties — e.g. line=1&quantity=3&properties[_ml]=ml=no — which strips
     _odm. A line with no _odm matches no ShipZip rate: "Shipping not
     available" at checkout. After ShipZip's writes settle, restamp any line
     missing _odm (a sibling's set, else the Standard default — the only value
     that can never unlock another rate). ShipZip still overwrites it when the
     customer picks a tile. Capped at 3 restores per 30s so it can never
     ping-pong with ShipZip. */
  var guardTimer = null, guardLog = [];
  function scheduleGuard(ms) {
    clearTimeout(guardTimer);
    guardTimer = setTimeout(guardCheck, ms || 1500);
  }
  function guardCheck() {
    guardTimer = null;
    var $j = jq();
    if (!$j) { return; }
    if (JK.writes > 0 || JK.removing || consolidating) { scheduleGuard(800); return; }
    var now = Date.now();
    guardLog = guardLog.filter(function (t) { return now - t < 30000; });
    if (guardLog.length >= 3) { return; }
    $j.getJSON('/cart.js?csapp=shipcs', function (cart) {
      var queue = [];
      (cart.items || []).forEach(function (i) {
        if (i.key && !hasRouting(i)) { addEntry(queue, i, defaultRouting); }
      });
      misrouted(cart).forEach(function (i) { addEntry(queue, i, alignRouting); });
      if (!queue.length) { return; }
      guardLog.push(Date.now());
      runQueue(queue);
    });
  }
  /* 2026-10-11: after an add, ONLY bring mismatched lines into line. Bare lines
     are left to ShipZip's own post-add write and the passes above (as before),
     so a stale cart attribute can't be stamped onto a brand-new line early. */
  var alignTimer = null;
  function scheduleAlign(ms) {
    clearTimeout(alignTimer);
    alignTimer = setTimeout(function () {
      alignTimer = null;
      var $j = jq();
      if (!$j) { return; }
      if (JK.writes > 0 || JK.removing || consolidating) { scheduleAlign(800); return; }
      var now = Date.now();
      guardLog = guardLog.filter(function (t) { return now - t < 30000; });
      if (guardLog.length >= 3) { return; }
      $j.getJSON('/cart.js?csapp=shipcs', function (cart) {
        var q = [];
        misrouted(cart).forEach(function (i) { addEntry(q, i, alignRouting); });
        if (!q.length) { return; }
        guardLog.push(Date.now());
        runQueue(q);
      });
    }, ms || 2500);
  }
  JK.scheduleAlign = scheduleAlign;
  /* Checkout-click hook (jokers-scan-reminder): align now, resolve when written.
     Resolves at once when nothing is misrouted; never rejects. */
  JK.alignNow = function () {
    return new Promise(function (resolve) {
      var $j = jq();
      if (!$j) { resolve(); return; }
      var t0 = Date.now();
      (function wait() {
        if ((JK.writes > 0 || JK.removing || consolidating) && Date.now() - t0 < 2500) { setTimeout(wait, 150); return; }
        $j.getJSON('/cart.js?csapp=shipcs', function (cart) {
          var q = [];
          misrouted(cart).forEach(function (i) { addEntry(q, i, alignRouting); });
          if (!q.length) { resolve(); return; }
          runQueue(q);
          (function settle() {
            if (JK.writes > 0 && Date.now() - t0 < 6000) { setTimeout(settle, 150); return; }
            resolve();
          })();
        }).fail(function () { resolve(); });
      })();
    });
  };
  (function () {
    var open = XMLHttpRequest.prototype.open, send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (m, u) { this.__jkGuardUrl = String(u || ''); return open.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function (body) {
      /* ShipZip's per-line writes start "line="; ours use "id=" */
      if (/\/cart\/change/.test(this.__jkGuardUrl || '') && typeof body === 'string' && body.indexOf('line=') === 0) {
        this.addEventListener('loadend', function () { scheduleGuard(1500); });
      }
      /* 2026-10-11: any add (FBT bundles, quick-add, apps) → check alignment once things settle */
      if (/\/cart\/add/.test(this.__jkGuardUrl || '')) {
        this.addEventListener('loadend', function () { scheduleAlign(2500); });
      }
      return send.apply(this, arguments);
    };
  })();

  sync();
})();

/* ---------- 5: fast remove ---------- */
(function () {
  var busy = false;
  var JK = window.JokersCart;

  /* Wait for any in-flight routing-flag write to finish. Two concurrent
     /cart/change.js calls are what made removing the last item flaky. */
  function whenIdle(cb, tries) {
    tries = tries || 0;
    if (JK.writes === 0 || tries > 160) { cb(); return; }
    setTimeout(function () { whenIdle(cb, tries + 1); }, 50);
  }

  function linePosition(li) {
    var siblings = Array.prototype.filter.call(li.parentNode.children, function (el) {
      return el.id && el.id.indexOf('CartDrawer-Item-') === 0;
    });
    return siblings.indexOf(li) + 1;
  }

  document.addEventListener('click', function (e) {
    if (busy || !e.target.closest) { return; }
    var a = e.target.closest('#CartDrawer a[is="cart-remove-button"], #CartDrawer a[href*="/cart/change"]');
    if (!a) { return; }
    var li = a.closest('li[id^="CartDrawer-Item-"]');
    var drawer = document.getElementById('CartDrawer');
    var sectionId = drawer && drawer.getAttribute('data-section-id');
    if (!li || !sectionId) { return; }

    e.preventDefault();
    e.stopPropagation();
    busy = true;
    JK.removing = true;

    var liId = li.id;
    var href = a.href;
    li.style.opacity = '0.5';
    li.style.pointerEvents = 'none';

    function finish() { JK.removing = false; busy = false; }

    whenIdle(function () {
      /* Re-find the row: a flag write that landed while we waited will
         have re-rendered the drawer and detached the original node.
         Remove by POSITION, never by line key — the routing flags rewrite
         line properties, which changes every key in the rendered markup,
         and the theme reacts to a stale key by redirecting to /cart. */
      var row = document.getElementById(liId) || li;
      if (!row.parentNode) { finish(); window.location.href = href; return; }
      var line = linePosition(row);
      if (line < 1) { finish(); window.location.href = href; return; }

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: 0, sections: [sectionId] }),
        credentials: 'same-origin'
      })
        .then(function (r) {
          /* Without this check a 4xx body parsed fine, cart.sections was
             undefined, no panel swap happened and the row just sat there. */
          if (!r.ok) { throw new Error('cart change failed: ' + r.status); }
          return r.json();
        })
        .then(function (cart) {
          /* Replace only the MiniCart panel — replacing #CartDrawer wholesale
             unbinds the close button and every other control. */
          var html = cart.sections && cart.sections[sectionId];
          var panel = document.getElementById('MiniCart-' + sectionId);
          if (html && panel) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var fresh = doc.getElementById('MiniCart-' + sectionId);
            if (fresh) { panel.innerHTML = fresh.innerHTML; }
          }
          /* The header count (and the drawer title count) sit outside the
             MiniCart panel, so the swap above never touches them. */
          document.querySelectorAll('cart-count').forEach(function (el) {
            el.textContent = cart.item_count;
            if (cart.item_count > 0) { el.removeAttribute('hidden'); } else { el.setAttribute('hidden', ''); }
          });
          finish();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        })
        .catch(function () {
          finish();
          window.location.href = href;
        });
    });
  }, true);
})();
/* ---------- Back from checkout (2026-09-25) ----------
   The browser restores the page from its cache with the drawer's old line
   keys, so +/- fails with "no valid id or line parameter". Reload instead
   (only when the cart has items, so ordinary Back navigation is untouched). */
window.addEventListener('pageshow', function (e) {
  if (e.persisted && document.querySelector('#CartDrawer li[id^="CartDrawer-Item-"]')) {
    window.location.reload();
  }
});

if (sessionStorage.getItem('jkOpenDrawer')) {
  sessionStorage.removeItem('jkOpenDrawer');
  window.addEventListener('load', function(){
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
  });
}

/* ---------- 7: make checkout prompts stand out (2026-09-25) ----------
   Styling only. Finds the two checkout prompts in the drawer by their
   wording (one is ours, one is ShipZip's) and tags them .jk-alert:
   bigger, bold, brand orange, pulsing. While the Delivery Option prompt
   is showing, the Delivery Option tab also gets a pulsing orange glow,
   which stops once an option is chosen. Pulse is off for reduced-motion. */
(function () {
  var TEXTS = ['Please select a Delivery Option', 'Please accept the Conditions of Sale'];
  var SPEED = '1.2s';   /* pulse speed: bigger number = slower */
  var st = document.createElement('style');
  st.textContent =
    '@keyframes jkAlertPulse{0%,100%{opacity:1}50%{opacity:.3}}' +
    '@keyframes jkTabGlow{0%,100%{opacity:0}50%{opacity:1}}' +
    '.jk-alert{font-size:16px !important;font-weight:700 !important;line-height:1.35 !important;' +
    'color:#F49F11 !important;animation:jkAlertPulse 1.8s ease-in-out infinite;}' +
    '.jk-alert-tab{position:relative;}.jk-alert-tab::after{content:"" !important;position:absolute !important;top:-8px;right:10px;bottom:-8px;left:-6px;border:2px solid #F49F11;border-radius:6px;pointer-events:none;animation:jkTabGlow 1.8s ease-in-out infinite;}' +
    '@media (max-width:767px){.jk-alert-tab::after{top:-4px;right:-4px;bottom:-4px;left:-4px;}}' +
    '@media (prefers-reduced-motion:reduce){.jk-alert,.jk-alert-tab::after{animation:none;}' +
    '.jk-alert-tab::after{opacity:1;}}';
  document.head.appendChild(st);

  var timer = null;
  function tag() {
    timer = null;
    var root = document.getElementById('CartDrawer');
    if (!root) { return; }
    var els = root.querySelectorAll('p, span, div, small');
    var deliveryShowing = false;
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.children.length) { continue; }
      var t = (el.textContent || '').trim();
      for (var j = 0; j < TEXTS.length; j++) {
        if (t.indexOf(TEXTS[j]) === 0) {
          if (!el.classList.contains('jk-alert')) { el.classList.add('jk-alert'); }
          /* offsetParent is null when the message (or a parent) is hidden */
          if (j === 0 && el.offsetParent !== null) { deliveryShowing = true; }
          break;
        }
      }
    }
    var tabs = root.querySelectorAll('[aria-controls^="ShippingOption-"]');
    for (var k = 0; k < tabs.length; k++) {
      if (tabs[k].closest('[id^="ShippingOption-"]')) { tabs[k].classList.remove('jk-alert-tab'); continue; } var has = tabs[k].classList.contains('jk-alert-tab');
      if (deliveryShowing && !has) { tabs[k].classList.add('jk-alert-tab'); }
      if (!deliveryShowing && has) { tabs[k].classList.remove('jk-alert-tab'); }
    }
  }
  function schedule() { if (!timer) { timer = setTimeout(tag, 150); } }

  var drawer = document.getElementById('CartDrawer');
  if (drawer) {
    new MutationObserver(schedule).observe(drawer, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['style', 'class', 'hidden', 'disabled']
    });
  }
  schedule();
})();

/* ---------- 6: re-add merges into the existing line (2026-09-25) ----------
   PROVEN CAUSE OF LOST UNITS (request log, preview 2026-09-25): after every
   add, ShipZip (csShippingAppV3) writes its delivery set onto each line by
   POSITION with a QUANTITY from its own snapshot (line=1&quantity=1&properties
   [_odm]=Shipping…). A re-add of the same size lands as a second, bare line;
   ShipZip's write makes it identical to the first, Shopify collapses the two
   and keeps only the quantity ShipZip sent — one unit gone. Happens on live.
   Fix: never let the second line exist. Before a /cart/add goes out, if a line
   with the same variant and the same non-routing properties is already in the
   cart, copy that line's routing/_ml properties (blanks included — Shopify
   treats a blank key as a difference) onto the add, so Shopify adds to the
   existing line's quantity. Never blocks an add: any failure → original add. */
(function () {
  if (!window.fetch) { return; }
  var COPY = ['_odm', '_odd', '_odate', '_odt', '_LocationId', '_ml'];
  var JK = window.JokersCart || { writes: 0 };
  var origFetch = window.fetch;

  function isAdd(url) {
    var u = typeof url === 'string' ? url : (url && url.url) || '';
    return /\/cart\/add(\.js)?(\?|$)/.test(u);
  }
  function core(p) {
    var o = {};
    Object.keys(p || {}).forEach(function (k) {
      if (COPY.indexOf(k) !== -1) { return; }
      if (p[k] === '' || p[k] === null || p[k] === undefined) { return; }
      o[k] = String(p[k]);
    });
    return o;
  }
  function sameCore(a, b) {
    var ka = Object.keys(a), kb = Object.keys(b);
    return ka.length === kb.length && ka.every(function (k) { return a[k] === b[k]; });
  }
  function readForm(body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var id = body.get('id');
      if (!id) { return null; }
      var props = {};
      body.forEach(function (v, k) { var m = k.match(/^properties\[(.+)\]$/); if (m) { props[m[1]] = v; } });
      return {
        id: String(id), props: props,
        apply: function (extra) {
          Object.keys(extra).forEach(function (k) { body.set('properties[' + k + ']', extra[k]); });
          return body;
        }
      };
    }
    if (typeof body === 'string' && body.charAt(0) === '{') {
      var obj = JSON.parse(body);
      var item = obj.items ? (obj.items.length === 1 ? obj.items[0] : null) : obj;
      if (!item || !item.id) { return null; }
      return {
        id: String(item.id), props: item.properties || {},
        apply: function (extra) {
          item.properties = Object.assign({}, item.properties || {}, extra);
          return JSON.stringify(obj);
        }
      };
    }
    return null;
  }
  /* Let an in-flight write settle first, so the line we copy from is final. */
  function idle() {
    return new Promise(function (res) {
      (function w(t) { if (!JK.writes || t > 60) { res(); return; } setTimeout(function () { w(t + 1); }, 50); })(0);
    });
  }

  /* ---- stale line keys on +/- (2026-09-25) ----
     A line's key changes whenever its properties are rewritten (ShipZip does
     this after every add; checkout does too). The drawer's +/- still holds
     the old key until it is redrawn, so Shopify answers "no valid id or line
     parameter". If a theme quantity change carries a key that no longer
     exists, look up the line's current key (same variant, only one such
     line) and send that instead. Our own writes (?csapp=) are untouched. */
  function isChange(url) {
    var u = typeof url === 'string' ? url : (url && url.url) || '';
    return /\/cart\/change(\.js)?(\?|$)/.test(u) && u.indexOf('csapp=') === -1;
  }
  function readId(body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var fid = body.get('id');
      return fid ? { id: String(fid), set: function (k) { body.set('id', k); return body; } } : null;
    }
    if (typeof body === 'string' && body.charAt(0) === '{') {
      var obj = JSON.parse(body);
      return obj.id ? { id: String(obj.id), set: function (k) { obj.id = k; return JSON.stringify(obj); } } : null;
    }
    if (typeof body === 'string' && body.indexOf('id=') !== -1) {
      var sp = new URLSearchParams(body), sid = sp.get('id');
      return sid ? { id: sid, set: function (k) { sp.set('id', k); return sp.toString(); } } : null;
    }
    return null;
  }
  function fixKey(url, init) {
    var r;
    try { r = readId(init.body); } catch (e) { r = null; }
    if (!r || r.id.indexOf(':') === -1) { return origFetch.call(window, url, init); }
    return origFetch.call(window, '/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.items) { return; }
        if (cart.items.some(function (i) { return i.key === r.id; })) { return; }
        var v = r.id.split(':')[0];
        var c = cart.items.filter(function (i) { return String(i.id) === v; });
        if (c.length === 1) { init.body = r.set(c[0].key); }
      })
      .catch(function () { /* never block the change */ })
      .then(function () { return origFetch.call(window, url, init); });
  }

  /* 2026-10-11: after any add, let the routing guard check delivery alignment */
  function afterAdd(p) {
    return p.then(function (res) {
      try { var g = window.JokersCart && window.JokersCart.scheduleAlign; if (g) { g(2500); } } catch (e) {}
      return res;
    });
  }
  window.fetch = function (url, init) {
    if (init && init.body && isChange(url)) { return fixKey(url, init); }
    if (isAdd(url)) { return afterAdd(addFetch.apply(window, arguments)); }
    return origFetch.apply(window, arguments);
  };
  function addFetch(url, init) {
    if (!init || !init.body || !isAdd(url)) { return origFetch.apply(window, arguments); }
    var f;
    try { f = readForm(init.body); } catch (e) { f = null; }
    if (!f) { return origFetch.apply(window, arguments); }
    return idle()
      .then(function () {
        return origFetch.call(window, '/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        var want = core(f.props);
        var line = cart && cart.items && cart.items.find(function (i) {
          return String(i.id) === f.id && sameCore(core(i.properties), want);
        });
        if (!line || !line.properties) { return; }
        var extra = {};
        COPY.forEach(function (k) {
          if ((k in line.properties) && !(k in f.props)) {
            extra[k] = line.properties[k] == null ? '' : String(line.properties[k]);
          }
        });
        if (Object.keys(extra).length) { init.body = f.apply(extra); }
      })
      .catch(function () { /* never block an add */ })
      .then(function () { return origFetch.call(window, url, init); });
  }
})();
